import {
    Card,
    Stack,
    Avatar,
    Typography,
    Link,
    Button,
    Chip,
    Box,
    Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDetailBarangModal } from "../../context/detailBarangModalContext";
import { useHapusStokModal } from "../../context/hapusStokModalContext";
import { useStokModal } from "../../context/stokModalContext";
import avatarAlt from "../../utils/avatarAlt";
import StarIcon from "@mui/icons-material/Star";
import toRibuan from "../../utils/toRibuan";
import toRupiah from "../../utils/toRupiah";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    ItemSatuan,
    DetailBundling,
    IOptions,
    ParamsGetWarehouse,
    ContentGetItemResponse,
} from "../../constants/types";
import { usePrintBarcodeModal } from "../../context/printBarcodeModal";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IconButton from "@mui/material/IconButton";
import useUser from "../../services/queries/useUser";
import useWarehouse from "../../services/queries/useWarehouse";
import { getLocalWarehouseId } from "../../utils/handleChangeWarehouseId";

interface ICardStokBarangProps {
    data: ContentGetItemResponse;
    handleSelectedProducts: (
        id: number,
        nama: string,
        harga: number,
        stok: number,
        stokPusat: number,
    ) => void;
    isSelected: (id: number) => boolean;
}

const CardStokBarang = ({
    data,
    handleSelectedProducts,
    isSelected,
}: ICardStokBarangProps) => {
    const {
        id,
        namaBarang,
        kategori,
        deskripsi,
        harga,
        multipleGambar,
        sku,
        stok: jumlahStok,
        stokPusat,
        detailBundling,
        favorite,
        hpp,
        hargaMultiple,
    } = data;
    const { openModal } = useStokModal();
    const { openModalHapusStok } = useHapusStokModal();
    const { openModalDetailBarang } = useDetailBarangModal();
    const { openModalPrintBarcode } = usePrintBarcodeModal();

    const truncate = (str: string) => {
        return str.length > 60 ? str.substring(0, 60) + "..." : str;
    };

    const { data: user } = useUser();

    const initialParamsWarehouse = {
        size: 500,
        page: 1,
        search: "",
    };

    const [paramsWarehouse, setParamsWarehouse] =
        React.useState<ParamsGetWarehouse>(initialParamsWarehouse);
    const { data: warehouse } = useWarehouse(paramsWarehouse);

    const warehouseId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalWarehouseId(user?.userId);
        }
        return null;
    }, [user]);

    const warehouseData = React.useMemo(() => {
        if (warehouseId) {
            return warehouse?.content.find((li) => li.id === warehouseId);
        }
        return warehouse?.content[0];
    }, [warehouseId, warehouse]);

    React.useEffect(() => {
        if (user && user?.warehouseId?.length > 0) {
            setParamsWarehouse((prev) => ({
                ...prev,
                userId: user.warehouseId[0],
            }));
        }
    }, [user]);

    return (
        <React.Fragment>
            <Card
                variant="outlined"
                sx={{
                    marginBottom: 1,
                    padding: 2,
                }}
            >
                <Chip label={kategori} color="primary" size="small" />
                <Stack
                    className="card-barang"
                    direction="column"
                    spacing={1}
                    justifyContent="space-between"
                    marginTop={1}
                >
                    <Stack direction="row" alignItems="center">
                        <IconButton
                            disabled={
                                warehouseData?.isDropship === 1
                                    ? stokPusat <= 0 && stokPusat !== null
                                    : jumlahStok <= 0 && jumlahStok !== null
                            }
                            onClick={() =>
                                handleSelectedProducts(
                                    id,
                                    namaBarang,
                                    harga,
                                    jumlahStok,
                                    stokPusat,
                                )
                            }
                            sx={{
                                minWidth: "unset",
                                padding: "6px",
                                width: "fit-content",
                                marginRight: 2,
                            }}
                            size="small"
                            color="primary"
                        >
                            {isSelected(id) ? (
                                <CheckBoxIcon />
                            ) : (
                                <CheckBoxOutlineBlankIcon />
                            )}
                        </IconButton>

                        <Link
                            onClick={() => openModalDetailBarang(data)}
                            color="#000000"
                            underline="none"
                            sx={{ cursor: "pointer" }}
                        >
                            <Stack direction="row" spacing={2}>
                                <Avatar
                                    sx={{
                                        backgroundColor: "primary.main",
                                        width: 78,
                                        height: 78,
                                        borderRadius: "10px",
                                    }}
                                    variant="square"
                                    alt={namaBarang}
                                    src={
                                        multipleGambar &&
                                        multipleGambar.length > 0
                                            ? multipleGambar[0].gambar
                                            : undefined
                                    }
                                >
                                    {avatarAlt(namaBarang)}
                                </Avatar>
                                {!!favorite && (
                                    <StarIcon
                                        sx={{
                                            position: "relative",
                                            zIndex: 2,
                                            bottom: 8,
                                            left: -34,
                                            border: 1,
                                            borderColor: "#e7e5e4",
                                            borderRadius: "50%",
                                            padding: 0.1,
                                            color: "#fbbf24",
                                            bgcolor: "#FFFFFF",
                                        }}
                                        fontSize="medium"
                                    />
                                )}
                                <Box>
                                    <Typography variant="subtitle2">
                                        {sku}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                    >
                                        {namaBarang}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                    >
                                        {toRupiah(harga)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Link>
                    </Stack>

                    <Link
                        onClick={() => openModalDetailBarang(data)}
                        color="#000000"
                        underline="none"
                        sx={{ cursor: "pointer" }}
                    >
                        <Stack direction="column" marginTop={1} spacing={1}>
                            <Typography variant="body2" component="div">
                                Stok Tersedia:{" "}
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color={
                                        warehouseData?.isDropship === 1
                                            ? stokPusat < 5 &&
                                              stokPusat !== null
                                                ? "#ef4444"
                                                : "#76747C"
                                            : jumlahStok < 5 &&
                                              jumlahStok !== null
                                            ? "#ef4444"
                                            : "#76747C"
                                    }
                                    display="inline"
                                >
                                    {warehouseData?.isDropship === 1
                                        ? stokPusat === null
                                            ? "Tidak Aktif"
                                            : stokPusat > 5
                                            ? toRibuan(stokPusat)
                                            : stokPusat <= 5 && stokPusat > 0
                                            ? toRibuan(stokPusat)
                                            : "Habis"
                                        : jumlahStok === null
                                        ? "Tidak Aktif"
                                        : jumlahStok > 5
                                        ? toRibuan(jumlahStok)
                                        : jumlahStok <= 5 && jumlahStok > 0
                                        ? toRibuan(jumlahStok)
                                        : "Habis"}
                                </Typography>
                            </Typography>
                            <Typography variant="body2">
                                {truncate(deskripsi ?? "Tidak ada deskripsi")}
                            </Typography>
                        </Stack>
                    </Link>
                    <Stack
                        display="flex"
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        direction="row"
                    >
                        <Stack direction="row" spacing={2}>
                            {/* <Button
                                variant="outlined"
                                onClick={() =>
                                    openModal({
                                        id: id,
                                        namaBarang: namaBarang,
                                        deskripsiBarang: deskripsi,
                                        kategoriId: kategoriId,
                                        harga: harga,
                                        sku: sku,
                                        jumlahStok: jumlahStok,
                                        stokPusat: stokPusat,
                                        isActiveStok: isActiveStok,
                                        itemSatuan: itemSatuan,
                                        isBundling,
                                        detailBundling,
                                        favorite: favorite,
                                        hargaMultiple: hargaMultiple,
                                    })
                                }
                                size="small"
                                sx={{
                                    minWidth: "unset",
                                    padding: "6px",
                                    width: "fit-content",
                                }}
                            >
                                <EditIcon />
                            </Button> */}
                            <Button
                                variant="contained"
                                onClick={() =>
                                    openModalPrintBarcode(
                                        sku,
                                        namaBarang,
                                        String(harga),
                                    )
                                }
                                size="small"
                                sx={{
                                    minWidth: "unset",
                                    padding: "6px",
                                    width: "fit-content",
                                }}
                            >
                                <PrintIcon />
                            </Button>
                            <Tooltip
                                title={
                                    warehouseData?.isDropship === 1
                                        ? "Tidak bisa menghapus produk dropship"
                                        : ""
                                }
                            >
                                <span>
                                    <Button
                                        disabled={
                                            warehouseData?.isDropship === 1
                                        }
                                        variant="contained"
                                        onClick={() =>
                                            openModalHapusStok(id, namaBarang)
                                        }
                                        size="small"
                                        color="error"
                                        sx={{
                                            minWidth: "unset",
                                            padding: "6px",
                                            width: "fit-content",
                                        }}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>
        </React.Fragment>
    );
};

export default CardStokBarang;
